import React, { useCallback, useEffect, useState } from "react";
import { Bookmark, BookmarkPlus, X, Share2, Building2 } from "lucide-react";
import { toast } from "sonner";
import { REGION_LABELS } from "./ScopeSelect";

const errMsg = (e) => e?.response?.data?.detail || e?.message || "Error";
const sig = (obj, keys) => JSON.stringify(keys.map((k) => (obj || {})[k] || ""));
const branchLabel = (b, zh) => (b === "all" ? (zh ? "全公司" : "All") : (REGION_LABELS[b] || {})[zh ? "zh" : "en"] || b);

/* 快捷视图（已签约客户 / 变更订单）：本人视图（≤8）+ 分公司共享视图（分公司经理 → 本分公司；副总 / 管理层 → 任意分公司 / 全公司）。scope 对应后端 /quick-views/{scope} */
export const QuickViews = ({ api, zh, scope, params, setParams, keys, testid = "quick-views" }) => {
  const [data, setData] = useState({ views: [], shared: [], canShare: false, shareBranches: [] });
  const [busy, setBusy] = useState(false);
  const [sharing, setSharing] = useState(null); // view being pushed
  const load = useCallback(() => api.get(`/quick-views/${scope}`).then(({ data: d }) => setData({ views: d.views || [], shared: d.shared || [], canShare: !!d.canShare, shareBranches: d.shareBranches || [] })).catch(() => {}), [api, scope]);
  useEffect(() => { load(); }, [load]);
  const { views, shared, canShare, shareBranches } = data;
  const cur = Object.fromEntries(keys.map((k) => [k, params.get(k) || ""]));
  const curSig = sig(cur, keys);
  const hasFilter = keys.some((k) => cur[k]);
  const persist = async (next) => {
    setBusy(true);
    try { const { data: d } = await api.put(`/quick-views/${scope}`, { views: next }); setData((p) => ({ ...p, views: d.views || [] })); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const saveCurrent = async () => {
    if (!hasFilter) { toast.error(zh ? "先设置筛选条件，再保存为视图" : "Set some filters first"); return; }
    if (views.length >= 8) { toast.error(zh ? "最多保存 8 个视图，请先删除一个" : "Max 8 views"); return; }
    const name = window.prompt(zh ? "视图名称（如：本月签约 · 有未收）" : "View name");
    if (!name || !name.trim()) return;
    await persist([...views, { name: name.trim().slice(0, 30), params: Object.fromEntries(keys.filter((k) => cur[k]).map((k) => [k, cur[k]])) }]);
    toast.success(zh ? "已保存视图" : "View saved");
  };
  const remove = async (v) => { if (window.confirm(zh ? `删除视图「${v.name}」？` : `Delete view "${v.name}"?`)) await persist(views.filter((x) => x.id !== v.id)); };
  const applyView = (v) => {
    const p = new URLSearchParams(params);
    keys.forEach((k) => (v.params[k] ? p.set(k, v.params[k]) : p.delete(k)));
    setParams(p, { replace: true });
  };
  const push = async (v, branch) => {
    setBusy(true);
    try {
      await api.post(`/quick-views/${scope}/shared`, { view: { name: v.name, params: v.params }, branch });
      toast.success(zh ? `已推送「${v.name}」到 ${branchLabel(branch, zh)}，该分公司同事打开本页即可一键使用` : `Pushed "${v.name}" to ${branchLabel(branch, zh)}`);
      setSharing(null); load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const revoke = async (s) => {
    if (!window.confirm(zh ? `撤回共享视图「${s.name}」（${branchLabel(s.branch, zh)}）？` : `Revoke shared view "${s.name}"?`)) return;
    try { await api.delete(`/quick-views/${scope}/shared/${s.id}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const Chip = ({ v, on, children, tid }) => (
    <span data-testid={tid} data-on={on ? 1 : 0} onClick={() => applyView(v)} title={zh ? "点击应用此视图" : "Apply view"}
      className={`group inline-flex items-center gap-1 rounded-full border text-xs pl-2.5 pr-1.5 py-1 cursor-pointer transition-colors ${on ? "bg-brand-grad text-white border-transparent shadow-brand" : "bg-white text-slate-600 border-slate-200 hover:border-brand-300"}`}>
      {children}
    </span>
  );
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid={testid} data-count={views.length} data-shared={shared.length}>
      <Bookmark className="h-3.5 w-3.5 text-slate-400" />
      {views.map((v) => {
        const on = sig(v.params, keys) === curSig;
        return (
          <Chip key={v.id} v={v} on={on} tid={`${testid}-chip-${v.id}`}>
            {v.name}
            {canShare && <button type="button" onClick={(e) => { e.stopPropagation(); setSharing(sharing?.id === v.id ? null : v); }} disabled={busy} className={`rounded-full p-0.5 ${on ? "hover:bg-white/20" : "hover:bg-brand-50 hover:text-brand-600"}`} data-testid={`${testid}-share-${v.id}`} title={zh ? "推送给分公司同事" : "Share with branch"}><Share2 className="h-3 w-3" /></button>}
            <button type="button" onClick={(e) => { e.stopPropagation(); remove(v); }} disabled={busy} className={`rounded-full p-0.5 ${on ? "hover:bg-white/20" : "hover:bg-red-50 hover:text-red-600"}`} data-testid={`${testid}-del-${v.id}`} title={zh ? "删除" : "Delete"}><X className="h-3 w-3" /></button>
          </Chip>
        );
      })}
      {shared.map((s) => {
        const on = sig(s.params, keys) === curSig;
        return (
          <span key={s.id} data-testid={`${testid}-shared-${s.id}`} data-on={on ? 1 : 0} data-branch={s.branch} onClick={() => applyView(s)} title={zh ? `${s.byName || s.by} 推送 · ${branchLabel(s.branch, zh)} · 点击应用` : `Shared by ${s.byName || s.by} · ${branchLabel(s.branch, zh)}`}
            className={`inline-flex items-center gap-1 rounded-full border border-dashed text-xs pl-2 pr-1.5 py-1 cursor-pointer transition-colors ${on ? "bg-amber-500 text-white border-transparent" : "bg-amber-50 text-amber-800 border-amber-300 hover:border-amber-500"}`}>
            <Building2 className="h-3 w-3" />{s.name}<span className={`text-[10px] ${on ? "text-white/80" : "text-amber-600"}`}>· {branchLabel(s.branch, zh)}</span>
            {s.canRevoke && <button type="button" onClick={(e) => { e.stopPropagation(); revoke(s); }} className={`rounded-full p-0.5 ${on ? "hover:bg-white/20" : "hover:bg-red-50 hover:text-red-600"}`} data-testid={`${testid}-revoke-${s.id}`} title={zh ? "撤回共享" : "Revoke"}><X className="h-3 w-3" /></button>}
          </span>
        );
      })}
      <button type="button" onClick={saveCurrent} disabled={busy} data-testid={`${testid}-save`} title={zh ? "把当前筛选保存为一键视图" : "Save current filters as a view"}
        className="inline-flex items-center gap-1 text-xs text-brand-700 border border-dashed border-brand-300 rounded-full px-2.5 py-1 hover:bg-brand-50 disabled:opacity-50">
        <BookmarkPlus className="h-3.5 w-3.5" />{zh ? "保存当前筛选" : "Save view"}{views.length ? ` ${views.length}/8` : ""}
      </button>
      {!views.length && !shared.length && <span className="text-[11px] text-slate-400">{zh ? "设好筛选后点「保存当前筛选」，下次一键打开" : "Set filters, then save them as a one-click view"}</span>}
      {sharing && (
        <span className="inline-flex items-center gap-1 text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded-full px-2 py-1" data-testid={`${testid}-share-panel`}>
          <Share2 className="h-3 w-3 text-brand-500" />{zh ? `推送「${sharing.name}」到：` : `Push "${sharing.name}" to:`}
          {shareBranches.map((b) => <button key={b} type="button" disabled={busy} onClick={() => push(sharing, b)} data-testid={`${testid}-share-to-${b}`} className="rounded-full bg-brand-500 hover:bg-brand-600 text-white px-2 py-0.5 font-semibold disabled:opacity-50">{branchLabel(b, zh)}</button>)}
          <button type="button" onClick={() => setSharing(null)} className="text-slate-400 hover:text-slate-700"><X className="h-3 w-3" /></button>
        </span>
      )}
    </div>
  );
};
